import { Dispatch, forwardRef, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, DateInput, Visible } from 'coreOld/components';
import { ArrowDown, Icon } from 'coreOld/elements';
import { DateOptions, DatePopperPlacement, Size } from 'coreOld/enums';
import { IDateOption } from 'coreOld/types';
import { formatDate } from 'coreOld/utils';

import {
  Box,
  ButtonsWrapper,
  Control,
  DateIntervalWrapper,
  DateWrapper,
  Dropdown,
  iconStyle,
  IconWrapper,
  ListItem,
  Menu,
  Placeholder,
  Text,
  Wrapper,
} from './dateIntervalStyle';

interface IProps {
  name: string;
  onApply?: (startDate: Date, endDate: Date) => void;
  handleChange: (value: IDateOption) => void;
  onChange?: (dates: Date[]) => void;
  setOpened?: Dispatch<SetStateAction<boolean>>;
  opened?: boolean;
  options: IDateOption[];
  selectedOption: IDateOption;
  startDate?: Date;
  endDate?: Date;
  placeholder?: string;
  size?: Size;
  rightPosition?: boolean;
  popperPlacement?: DatePopperPlacement;
}

const DateIntervalView = forwardRef<HTMLElement, IProps>(function DateIntervalView(
  {
    handleChange,
    options,
    selectedOption,
    setOpened,
    opened,
    onChange,
    startDate,
    endDate,
    onApply,
    placeholder = 'Created',
    size,
    rightPosition = false,
    popperPlacement = DatePopperPlacement.BOTTOM,
  }: IProps,
  ref
) {
  const { t } = useTranslation();

  return (
    <DateIntervalWrapper ref={ref}>
      <Control onClick={() => setOpened((currValue) => !currValue)} size={size}>
        <Box>
          <Placeholder>
            <Icon name={Icon.CALENDAR} style={iconStyle} />

            <Visible when={!selectedOption || !startDate || !endDate}>{placeholder}</Visible>

            <Visible when={Boolean(selectedOption && startDate && endDate)}>
              <Text>
                {formatDate(startDate)} - {formatDate(endDate)}
              </Text>
            </Visible>
          </Placeholder>
        </Box>

        <IconWrapper>
          <ArrowDown />
        </IconWrapper>
      </Control>

      <Visible when={opened}>
        <Dropdown
          isNotFittingToTheRight={
            rightPosition && selectedOption?.label === DateOptions.CUSTOM_RANGE
          }
          placement={popperPlacement}
        >
          <Wrapper>
            <Menu>
              {options.map((item) => (
                <ListItem
                  key={item.id}
                  selected={selectedOption?.id === item.id}
                  onClick={() => handleChange(item)}
                >
                  {item.label}
                </ListItem>
              ))}
            </Menu>

            <Visible when={selectedOption?.label === DateOptions.CUSTOM_RANGE}>
              <DateInput
                selected={endDate}
                endDate={endDate}
                startDate={startDate}
                onChange={onChange}
                monthsShown={2}
                selectsRange
                inline
              />
            </Visible>
          </Wrapper>

          <Visible when={selectedOption?.label === DateOptions.CUSTOM_RANGE}>
            <ButtonsWrapper>
              <DateWrapper>
                {formatDate(startDate)} - {endDate ? formatDate(endDate) : ''}
              </DateWrapper>

              <Button onClick={() => setOpened(false)} size={Size.XS}>
                {t('buttonLabelCancel')}
              </Button>

              <Button
                onClick={() => onApply(startDate, endDate)}
                size={Size.XS}
                disabled={!endDate}
              >
                {t('buttonLabelApply')}
              </Button>
            </ButtonsWrapper>
          </Visible>
        </Dropdown>
      </Visible>
    </DateIntervalWrapper>
  );
});

export default DateIntervalView;
